/**
 * Shared shapes for the experiment dataset.
 *
 * These are the types every experiment screen consumes. When the experiments
 * graduate and `useExperimentDataset` becomes a real Influx/API query, this
 * file is the contract that has to keep holding — see
 * docs/adr/0014-experiment-dataset-seam.md.
 */

import type { ClimateBand } from "./envelopes";

/* ------------------------------------------------------------------ fleet -- */

export const FLEET_COUNTRIES = [
  "Belgium",
  "Netherlands",
  "France",
  "Germany",
  "United Kingdom",
  "Spain",
  "Italy",
  "Other",
] as const;
export type FleetCountry = (typeof FLEET_COUNTRIES)[number];

export const BACKHAULS = ["lorawan", "cellular", "wifi"] as const;
export type Backhaul = (typeof BACKHAULS)[number];

export const BACKHAUL_NAME: Record<Backhaul, string> = {
  lorawan: "LoRaWAN gateways",
  cellular: "Cellular (NB-IoT / LTE-M)",
  wifi: "Wi-Fi bridges",
};

export const DEVICE_HEALTHS = ["critical", "warning", "healthy", "paused"] as const;
export type DeviceHealth = (typeof DEVICE_HEALTHS)[number];

export const ISSUE_KINDS = [
  "stale-data",
  "gateway-offline",
  "weak-backhaul",
  "low-battery",
] as const;
export type IssueKind = (typeof ISSUE_KINDS)[number];

export interface FleetIssue {
  kind: IssueKind;
  /** Short human title, e.g. "Gateway offline". */
  title: string;
  /** The identifying detail line, e.g. "GW-NAM-01 · LoRaWAN · since Feb 12". */
  detail: string;
  /** ISO timestamp the issue was first seen. */
  since: string;
  /**
   * The device this issue is about.
   *
   * Load-bearing for anonymisation: issues are a *filtered* subsequence of a
   * site's devices, so rebuilding the detail line by position would attribute
   * an issue to whichever device happened to sit at the same index.
   */
  deviceId: string;
  /** The site this issue is about. */
  siteId: string;
}

export interface FleetDevice {
  id: string;
  /** Device reference as it appears in ops tooling. */
  reference: string;
  siteId: string;
  /** Gateways route traffic; loggers produce it. */
  role: "gateway" | "logger";
  backhaul: Backhaul;
  health: DeviceHealth;
  /** ISO timestamp of the most recent reading. */
  lastReadingAt: string;
  messagesPerHour: number;
}

export interface FleetSite {
  id: string;
  /** 1-based position, the basis of the `#NNN` anonymised alias. */
  index: number;
  /** Real display name. Replaced by `alias` when anonymised. */
  name: string;
  /** Stable anonymised alias, e.g. "Site #014". */
  alias: string;
  city: string;
  country: FleetCountry;
  buildingType: BuildingType;
  latitude: number;
  longitude: number;
  backhaul: Backhaul;
  /** Worst-of rollup across the site's devices. */
  health: DeviceHealth;
  gatewayCount: number;
  loggerCount: number;
  messagesPerHour: number;
  lastReadingAt: string;
  issues: FleetIssue[];
}

export interface HeadquartersNode {
  name: string;
  city: string;
  country: FleetCountry;
  latitude: number;
  longitude: number;
  /** Messages per minute arriving at ingest. */
  ingestRatePerMinute: number;
  messagesToday: number;
  averageLatencySeconds: number;
  uptime30dPercent: number;
}

export interface ThroughputPoint {
  /** ISO timestamp, on the hour. */
  at: string;
  messagesPerMinute: number;
}

/**
 * A site changing health inside the replay window.
 *
 * Carries ids only — never a name — so `anonymise.ts` has nothing to rewrite
 * and the timeline cannot become a leak. Consumers resolve the label from
 * `sites`, which is already anonymised by the time they see it.
 */
export interface FleetStatusEvent {
  siteId: string;
  /** ISO timestamp of the change. */
  at: string;
  from: DeviceHealth;
  to: DeviceHealth;
}

export interface FleetSummary {
  siteCount: number;
  deviceCount: number;
  gatewayCount: number;
  loggerCount: number;
  messagesPerMinute: number;
  health: Record<DeviceHealth, number>;
  /** Degraded / improved since yesterday 08:00, as the mockups report it. */
  degradedSinceYesterday: number;
  improvedSinceYesterday: number;
}

export interface CountryRollup {
  country: FleetCountry;
  siteCount: number;
  deviceCount: number;
}

export interface BackhaulRollup {
  backhaul: Backhaul;
  gatewayCount: number;
  deviceCount: number;
}

export interface FleetDataset {
  sites: FleetSite[];
  devices: FleetDevice[];
  headquarters: HeadquartersNode;
  summary: FleetSummary;
  byCountry: CountryRollup[];
  byBackhaul: BackhaulRollup[];
  /** 24 h of hourly throughput, for the replay scrubber and the sparkline. */
  throughput24h: ThroughputPoint[];
  alerts: FleetIssue[];
  /**
   * Health changes inside the replay window, oldest first.
   *
   * What the scrubber actually replays: `sites[].health` is the state *now*, and
   * reversing the events newer than an instant reconstructs the state then. It
   * is also where `degradedSinceYesterday` / `improvedSinceYesterday` come
   * from, so the strip's delta line and the scrubber's ticks cannot disagree.
   */
  timeline24h: FleetStatusEvent[];
}

/* ---------------------------------------------------------------- climate -- */

/** The four building types the **generator** deals from, each with its own climate character. */
export const BUILDING_TYPES = ["Museum", "Archive", "Church", "Castle"] as const;

/**
 * A building type, as a **display label**.
 *
 * Widened from the literal union above because the real taxonomy lives in `@charphq/shared` (nine
 * slugs, and growing) and arrives here already resolved to a label — deployment overriding
 * customer, with an explicit wording for the unspecified case. The generator's four remain a
 * subset of what can appear.
 */
export type BuildingType = string;

/** The six regions the **generator** deals from. Real data derives its own — see `ClimateRegion`. */
export const CLIMATE_REGIONS = [
  "Flanders",
  "Netherlands",
  "Wallonia",
  "Brussels",
  "Northern France",
  "Rhineland",
] as const;

/**
 * A region name.
 *
 * Widened from the literal union above because real regions are **derived** from a country and a
 * postal code (`deriveRegion` in `@charphq/shared`), so the set grows with the rule table and with
 * wherever CHARP takes on a customer next. Group keys are strings on this screen anyway — nothing
 * switches on a specific region.
 */
export type ClimateRegion = string;

/**
 * Climate years run September → August, as the mockups' axes do.
 *
 * These three are what the **generator** produces. They are no longer the authority for what is
 * selectable: real data supplies whatever Influx actually holds, which may be one year or five, so
 * a screen reads `ClimateDataset.years` and never this constant.
 */
export const CLIMATE_YEARS = ["2023-24", "2024-25", "2025-26"] as const;

/**
 * A climate year id in canonical `YYYY-YY` form, e.g. `"2025-26"`.
 *
 * Was a literal union of the three above, which made a real dataset unrepresentable. The shape is
 * validated by {@link isClimateYearId} at every boundary that accepts one from outside — a URL
 * parameter, a stored link — and `ClimateDataset.years` says which are actually available.
 */
export type ClimateYear = string;

/** The canonical shape. Kept local rather than imported from `@charphq/shared`: `search.ts` runs
 *  in the router's `validateSearch`, which is reachable from the entry chunk, and a regex is
 *  cheaper than pulling the shared bundle onto that path. */
const CLIMATE_YEAR_PATTERN = /^\d{4}-\d{2}$/;

/** Is this a well-formed climate year id? Says nothing about whether data exists for it. */
export function isClimateYearId(value: unknown): value is ClimateYear {
  return typeof value === "string" && CLIMATE_YEAR_PATTERN.test(value);
}

export interface ClimateSite {
  id: string;
  index: number;
  name: string;
  alias: string;
  region: ClimateRegion;
  country: FleetCountry;
  buildingType: BuildingType;
  loggerCount: number;
}

export interface ClimatePoint {
  /** ISO date of the period start. */
  date: string;
  /** Week 1–52 for weekly points, month 1–12 for monthly points. */
  period: number;
  temperature: number;
  relativeHumidity: number;
  /** Mean daily temperature swing over the period, in °C. */
  dailySwing: number;
  band: ClimateBand;
}

export interface ClimateSiteYear {
  siteId: string;
  year: ClimateYear;
  weekly: ClimatePoint[];
  monthly: ClimatePoint[];
}

/** Which series a group summary's band shares were computed from. */
export type ClimateSummaryResolution = "weekly" | "monthly";

/** Aggregate over an arbitrary grouping (region, country, type, or site). */
export interface ClimateGroupSummary {
  key: string;
  label: string;
  /**
   * The sites behind this group, in dataset order.
   *
   * Ids only — `cs-014`, never a name — so this is safe to carry through the
   * anonymisation layer untouched. Consumers needing a per-site breakdown (the
   * Climate Loop's tooltip, its isolate round-trip) resolve names from
   * `climate.sites`, which is already anonymised by the time they see it.
   */
  siteIds: string[];
  siteCount: number;
  loggerCount: number;
  meanTemperature: number;
  meanRelativeHumidity: number;
  /** Mean daily temperature swing, in °C. */
  dailySwing: number;
  /** Share of periods classified `safe`, 0–1. */
  safeFraction: number;
  /** Share of periods per band, 0–1, summing to 1. */
  bandFractions: Record<ClimateBand, number>;
  /**
   * Which series the band shares were computed from.
   *
   * `weekly` is the honest resolution for excursions — a month can average inside the envelope
   * while containing two damp weeks. A real dataset may only carry monthly means, in which case
   * the shares are coarser and a screen showing them should say so rather than implying a
   * precision it does not have.
   */
  resolution: ClimateSummaryResolution;
  /** How many periods the shares were computed over, at `resolution`. */
  periodCount: number;
  /** Months (1–12, calendar) containing at least one excursion. */
  excursionMonths: number[];
  /** Bands responsible for this group's excursions. */
  excursionKinds: ClimateBand[];
  /** Mean-temperature delta against the previous year, in °C. `null` for the first year. */
  temperatureDeltaVsLastYear: number | null;
  /** Monthly mean series, for the sparkline columns in the mockups. */
  monthly: ClimatePoint[];
}

export interface ClimateDataset {
  sites: ClimateSite[];
  series: ClimateSiteYear[];
  years: readonly ClimateYear[];
  regionCounts: Record<ClimateRegion, number>;
  buildingTypeCounts: Record<BuildingType, number>;
}

/* --------------------------------------------------------------- combined -- */

export type GroupBy = "region" | "country" | "buildingType" | "site";
export type Metric = "temperature" | "humidity";
export type Source = "historical" | "live";

export interface ExperimentDataset {
  fleet: FleetDataset;
  climate: ClimateDataset;
  /** True when names have been replaced by `#NNN` aliases. */
  anonymised: boolean;
  /** The injected "now" the whole dataset was generated against. */
  today: string;
  seed: number;
}
